import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { DatetimeFormatType, DatetimeMonthFormat, TypeOfDate } from 'app/lib/datetime/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import Datetime from 'app/design-system/components/Datetime';
import { useTranslation } from 'app/lang';
import { SlotType } from 'app/lib/betting/betting-entities';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import LiveBadge, { BadgePreset } from 'app/widgets/LiveBadge';

import { RemoveConflictedEntries, removeConflictedEntries, ResolveByReplaceEntries } from '../../../actions/conflict';
import { InsertInQueueForDelete, insertInQueueForDelete } from '../../../actions/entries';
import { MATCH_ERROR_DICTIONARY, MatchErrorCode, SubmissionState } from '../../../constants';
import type { RecordOfEntryErrorType, RecordOfMatchEntryType } from '../../../records';
import { selectIsConflictedMatch } from '../../../selectors/conflict';
import { selectMatches } from '../../../selectors/entries';
import { selectExpressInsideMatchError, selectExpressMatchPrices } from '../../../selectors/stakeTypes/express';
import { selectConflictState } from '../../../selectors/state';
import { selectSubmission } from '../../../selectors/submission';
import type { ConflictState } from '../../../types/conflict';
import { isReplaceAction } from '../../../utils/conflict';
import Error from '../../EntryComponents/Error';
import Name from '../../EntryComponents/Name';
import RemoveButton from '../../EntryComponents/RemoveButton';
import PriceDiff from '../../PriceDiff';
import Entry from './Entry';

import dictionary from './ExpressInsideMatch.dictionary.json';

import entryStyles from '../../EntryComponents/EntryComponents.module.scss';
import styles from './ExpressInsideMatch.module.scss';

// ---

type StoreProps = {
  betIds?: RecordOfMatchEntryType['betIds'];
  matchName?: string;
  matchStartDate?: Date;
  isOpen: boolean;
  entryPrice?: number;
  entityPrice?: number;
  disabled: boolean;
  error: RecordOfEntryErrorType | null;
  conflict: ConflictState;
  isConflictedMatch: boolean;
  isLive: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<InsertInQueueForDelete | RemoveConflictedEntries>;
};

type ExpressInsideMatchProps = {
  matchId: number;
};

const ExpressInsideMatch: FC<ExpressInsideMatchProps & DispatchProp & StoreProps> = ({
  matchId,
  betIds,
  matchName,
  matchStartDate,
  isOpen,
  entryPrice,
  entityPrice,
  disabled,
  error,
  conflict,
  isConflictedMatch,
  isLive,
  dispatch,
}) => {
  const { t } = useTranslation();
  const hasConflict = conflict !== null;

  const removeAllEntriesOfOneMatchHandler = useCallback(() => {
    if (betIds) {
      const ids = betIds.toArray();

      if (hasConflict) {
        dispatch(removeConflictedEntries(ids));
      }
      else {
        dispatch(insertInQueueForDelete(ids));
      }
    }
  }, [betIds, hasConflict]);

  const hasReplaceAction = useMemo(() => (
    conflict?.actions.find((action) => (
      isReplaceAction(action)
    )) as (ResolveByReplaceEntries | undefined)
  ), [conflict?.actions]);

  if (!betIds) {
    console.error('ExpressInsideMatch -> `betIds` not found');
    return null;
  }

  const _className = classNames(styles.container, {
    [styles.disabledEntry]: disabled,
    [styles.notConflictedEntry]: hasConflict && !isConflictedMatch,
  });

  const renderFooter = () => {
    if (isConflictedMatch) {
      return (
        <Error message={t(dictionary.errorMessage)}
               data-test="expressInsideMatch-error" />
      );
    }

    if ((error || !isOpen) && !disabled) {
      return (
        <Error message={isOpen ? '' : MATCH_ERROR_DICTIONARY[MatchErrorCode.RECEPTION_CLOSED]}
               error={isOpen ? error : null}
               data-test="expressInsideMatch-error" />
      );
    }

    return (
      <>
        {isLive && (
          <LiveBadge preset={BadgePreset.BET_LIVE} />
        )}
        <Datetime value={matchStartDate}
                  title={t(dictionary.dateTimeTitle)}
                  data-test="expressInsideMatch-datetime"
                  className={entryStyles.datetime}
                  format={DatetimeFormatType.LONG}
                  typeOfDate={TypeOfDate.MATCH_DATE}
                  monthFormat={DatetimeMonthFormat.SHORT} />

        <div className={styles.priceDiffContainer}>
          <PriceDiff data-test="expressInsideMatch-priceDiff"
                     prev={entryPrice}
                     next={disabled ? entryPrice : entityPrice} />
        </div>
      </>
    );
  };

  const replacedMatchName = replacingHyphenOnDash(matchName);

  return (
    <div className={_className}
         data-scroll={matchId}
         data-test="expressInsideMatch-container">
      <header className={styles.header}
              data-test="expressInsideMatch-header">
        <span className={styles.icon}
              data-test="expressInsideMatch-icon">
          &#128077;
        </span>
        {t(dictionary.headerText)}
        {(!hasConflict || hasReplaceAction) && (
          <RemoveButton disabled={disabled}
                        data-test="expressInsideMatch-removeButton"
                        onClick={removeAllEntriesOfOneMatchHandler} />
        )}
      </header>
      <div className={styles.bets}
           data-test="expressInsideMatch-entries">
        {betIds.map((betId) => (
          <Entry key={betId}
                 betId={betId} />
        ))}
      </div>

      <Name value={replacedMatchName}
            error={!!error}
            data-test="expressInsideMatch-matchName"
            className={classNames(entryStyles.matchName, styles.matchName)} />

      <footer className={styles.footer}
              data-test="expressInsideMatch-footer">
        {renderFooter()}
      </footer>
    </div>
  );
};

const mapStateToProps = (state: ReduxState, { matchId }: ExpressInsideMatchProps): StoreProps => {
  const match = selectMatches(state).get(matchId);
  const prices = selectExpressMatchPrices(state).get(matchId);

  return {
    betIds: match?.betIds,
    matchName: match?.entity.name,
    matchStartDate: match?.entity.startDate,
    isOpen: match?.entity.isOpen ?? false,
    entryPrice: prices?.entryPrice,
    entityPrice: prices?.entityPrice,
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    error: selectExpressInsideMatchError(state),
    conflict: selectConflictState(state),
    isConflictedMatch: selectIsConflictedMatch(state, matchId),
    isLive: match?.entity.slotType === SlotType.LIVE,
  };
};

export default connect(mapStateToProps)(ExpressInsideMatch);
